import React from 'react';
import { Router } from 'react-router-dom';
import PropTypes from 'prop-types';
import { render } from '@testing-library/react';
// eslint-disable-next-line
import { createMemoryHistory } from 'history';

export function formatPhoneNumber(phoneNumberString) {
  const cleaned = `${phoneNumberString}`.replace(/\D/g, '');
  const match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    const intlCode = match[1] ? '+1 ' : '';
    return [intlCode, '(', match[2], ') ', match[3], '-', match[4]].join('');
  }
  return null;
}

export default function numberWithCommas(x) {
  return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

export function getGrowthPercentage(firstValue, secondValue, digits = 2) {
  if (firstValue === 0) {
    return 100;
  }
  if (!((secondValue * 100) / firstValue - 100)) {
    return 0;
  }
  return ((secondValue * 100) / firstValue - 100).toFixed(digits);
}

export function renderWithRouter(
  ui,
  { route = '/', history = createMemoryHistory({ initialEntries: [route] }) } = {}
) {
  const Wrapper = ({ children }) => <Router history={history}>{children}</Router>;
  Wrapper.propTypes = {
    children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
  };
  return {
    ...render(ui, { wrapper: Wrapper }),
    history,
  };
}

export const dateSortAsc = (date1, date2) => {
  // This is a comparison function that will result in dates being sorted in
  // ASCENDING order. As you can see, JavaScript's native comparison operators
  // can be used to compare dates. This was news to me.
  if (date1 > date2) return 1;
  if (date1 < date2) return -1;
  return 0;
};
export const sortArrayOfDates = (arr, monthLength = 'short') => {
  const overallMonths = [];
  arr.forEach((key) => {
    overallMonths.push(new Date(`01 ${key}`));
  });
  overallMonths.sort(dateSortAsc);
  overallMonths.forEach((item, index) => {
    overallMonths[index] = `${encodeURIComponent(
      item.toLocaleString('en-US', { month: monthLength })
    )} ${item.getFullYear()}`;
  });
  return overallMonths;
};

export const fallbackCopyTextToClipboard = (text, cb = () => {}) => {
  const textArea = document.createElement('textarea');
  textArea.value = text;
  textArea.style.top = '0';
  textArea.style.left = '0';
  textArea.style.position = 'fixed';
  document.body.appendChild(textArea);
  textArea.focus();
  textArea.select();
  try {
    const successful = document.execCommand('copy');
    // const msg = successful ? 'successful' : 'unsuccessful';
    // console.log(`Fallback: Copying text command was ${msg}`);
    if (successful) {
      cb();
    }
  } catch (e) {
    //
  }
  document.body.removeChild(textArea);
};

export const copyTextToClipboard = (text, cb = () => {}) => {
  if (!navigator.clipboard) {
    fallbackCopyTextToClipboard(text, cb);
    return;
  }
  navigator.clipboard.writeText(text).then(() => {
    cb();
  });
};

export const combineObjects = (obj1, obj2) => {
  const combinedObj = { ...obj1 };

  Object.keys(obj2).forEach((key) => {
    combinedObj[key] = (combinedObj[key] || 0) + obj2[key];
  });

  return combinedObj;
};


export function trimStartString(str, substring) {
  if (str.startsWith(substring)) {
      return str.slice(substring.length);
  }
  return str;
}

export function convertToTitleCase(str) {
  const words = str?.split('_');
  const titleCaseWords = words?.map(word => word.charAt(0).toUpperCase() + word.slice(1));
  return titleCaseWords?.join(' ') || "";
}

// The /v1/analytics/monthly pipeline emits a row for the most recent month
// before it has been populated (e.g. on the 1st of the month), with every
// metric at 0. Consumers treat the last month as "current", so that empty row
// makes charts crash to zero and the Organic Snapshot cards show a false
// "no access" state. Trim the trailing run of all-zero months so the latest
// populated month is treated as current. Only the tail is trimmed; earlier
// months (including any genuine zero months) are preserved. A site with no
// data at all collapses to [], which the consumers already handle as "empty".
export function dropTrailingEmptyMonths(history) {
  if (!Array.isArray(history)) return [];
  let lastWithData = -1;
  history.forEach((month, index) => {
    const values = month?.values || {};
    const hasData =
      values.ga_sessions_organic ||
      values.ga_goalCompletionsAll_organic ||
      values.gsc_queries ||
      values.gsc_impressions;
    if (hasData) lastWithData = index;
  });
  return history.slice(0, lastWithData + 1);
}
